import { useEffect } from 'react'
import { useAccountMeQuery } from '~/api/accounts'
import {
  BaseVisibleFieldsSettingsResponse,
  ObjId,
  UserPreferenceNamespace,
  UserPreferenceType,
  useVisibleFieldsMutation,
  useVisibleFieldsQuery,
  useVisibleFieldsSettingsQuery,
  VisibleFieldData,
  VisibleFieldPosition
} from '~/api/userPreferences'
import { useMsg } from '~/common/localizationUtils'
import { ClientOnly } from '~/common/nextJsUtils'
import { useActionBarContext } from '~/components/v2/ResultsLayout/ActionBar/ActionBar.hook'
import { forwardRef, IconButton, IconButtonProps } from '~/design_system'
import { Cog } from '~/icons'
import { CustomizeDefaultDataModal } from '~/settings/list_view_defaults/CustomizeDefaultDataModal'

type VisibleFieldButtonProps = Omit<IconButtonProps, 'aria-label'> & {
  value?: VisibleFieldData[]
  onChange?: (value: VisibleFieldData[]) => void
  // if permission checking for fields is needed, pass in the object id with the necessary org unit to check
  // against user's permissions
  objId?: ObjId
  onSuccess?: () => void
}

export const VisibleFieldsButton = forwardRef<VisibleFieldButtonProps, 'div'>(
  (
    { onChange: propsOnChange, value: propsValue, objId: propsObjId, onSuccess: propsOnSuccess, css: _css, ...props },
    ref
  ) => {
    const { onToggle = () => {} } = useActionBarContext()?.visibleFieldsDisclosure ?? {}
    const namespace = useActionBarContext()?.userPreferenceNamespace ?? UserPreferenceNamespace.JOB
    const { visibleFields: visibleFieldsContext } = useActionBarContext()
    const value = propsValue ?? visibleFieldsContext?.value ?? []
    const onChange = propsOnChange ?? visibleFieldsContext?.onChange
    const msg = useMsg()
    const label = msg('settings.list_card_view_defaults.customize_default_data.modal.title')

    const { isEmployerRole, isLoading: isAccountsMeLoading } = useAccountMeQuery()
    const {
      visible_fields_client_data,
      visible_fields_vendor_data,
      isLoading: isClientLoading
    } = useVisibleFieldsSettingsQuery(namespace, propsObjId)
    const {
      visible_fields_user_data,
      visible_fields_id,
      isLoading: isUserLoading
    } = useVisibleFieldsQuery(UserPreferenceType.VISIBLE_FIELDS, namespace, propsObjId)
    const { mutateAsync: visibleFieldsMutate } = useVisibleFieldsMutation(visible_fields_id, propsObjId)

    useEffect(() => {
      if (isClientLoading || isUserLoading || isAccountsMeLoading) return
      const default_visible_fields_data = isEmployerRole ? visible_fields_client_data : visible_fields_vendor_data
      const default_visible_fields = getVisibleFieldsData(default_visible_fields_data)
      const userFieldsOrDefaultFields: VisibleFieldData[] = visible_fields_user_data ?? default_visible_fields
      onChange(userFieldsOrDefaultFields)
    }, [
      isClientLoading,
      isUserLoading,
      isAccountsMeLoading,
      isEmployerRole,
      visible_fields_client_data,
      visible_fields_vendor_data
    ])

    return (
      <>
        <ClientOnly>
          <IconButton
            ref={ref}
            data-visible-fields-button
            variant="secondary"
            textStyle="heading2"
            icon={<Cog />}
            onClick={onToggle}
            showTooltip
            aria-label={label}
            {...props}
          />
        </ClientOnly>
        <CustomizeDefaultDataModal
          initialValues={value}
          applyAction={visibleFieldsMutate}
          userApplyOnChange={onChange}
          onSuccess={propsOnSuccess}
        />
      </>
    )
  }
)

function getVisibleFieldsData(visibleFieldsData: BaseVisibleFieldsSettingsResponse | undefined) {
  const data = visibleFieldsData?.fields
  const defaultData = visibleFieldsData?.default_fields
  let visibleFields: string | any[] = []

  if (data && Object.keys(data).length !== 0) {
    visibleFields = data
  } else if (defaultData && Object.keys(defaultData).length !== 0) {
    visibleFields = defaultData
  }
  return visibleFields
}

export const useVisibleFields = () => {
  return (
    useActionBarContext()
      ?.visibleFields?.value?.filter(
        visibleField =>
          visibleField.value &&
          !visibleField.blocked &&
          visibleField.position === VisibleFieldPosition.ATTRIBUTES_COLUMN
      )
      .sort((a, b) => a.display_order - b.display_order) || []
  )
}

export const useGetVisibleFieldsByPosition = (position: VisibleFieldPosition): Record<string, VisibleFieldData> => {
  const fields =
    useActionBarContext()
      ?.visibleFields?.value?.filter(field => field.position === position)
      .sort((a, b) => a.display_order - b.display_order) || []
  return Object.fromEntries(fields.map(field => [field.key, field]))
}
